import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInvoiceEditData } from '../../../../features/customer/customer.slice';

interface TimesheetDataEntry {
  id: number;
  workingDate: string;
  dateId: string;
  number: number;
  unit: string;
  vat: number;
  isArticleShownInInvoice: boolean;
  amountPerUnit: number;
  amount: number;
  articleNumber: string;
  workingDescription: string;
  houseworkType: string;
  workingHour: number;
  CustomWorkingDescription: string;
  userId: number;
}

interface AddTimesheetSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TimesheetFormData, timesheetDataArray: TimesheetDataEntry[]) => void;
}

interface TimesheetFormData {
  clientDetails: string;
  taskDetails: string;
  fromDate: string;
  toDate: string;
  hours: string;
  pricingType: string;
  price: string;
}

const AddTimesheetSidebar: React.FC<AddTimesheetSidebarProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Get user ID from Redux auth state
  const userId = useSelector((state: any) => state.auth?.user?.userId);
  
  // Get invoice edit data (including customers) from Redux customer state
  const { invoiceEditData, loading } = useSelector((state: any) => state.customer);
  
  // Extract customers from invoiceEditData
  const customers = invoiceEditData?.response?.customers || [];

  const [formData, setFormData] = useState<TimesheetFormData>({
    clientDetails: '',
    taskDetails: '',
    fromDate: '',
    toDate: '',
    hours: '',
    pricingType: 'Hourly',
    price: ''
  });

  const [validationErrors, setValidationErrors] = useState<{
    clientDetails?: string;
    taskDetails?: string;
    fromDate?: string;
    toDate?: string;
    hours?: string;
    pricingType?: string;
    price?: string;
  }>({});

  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
  const [isClientDropdownOpen, setIsClientDropdownOpen] = useState(false);
  const [isPricingDropdownOpen, setIsPricingDropdownOpen] = useState(false);
  const clientDropdownRef = React.useRef<HTMLDivElement>(null);
  const pricingDropdownRef = React.useRef<HTMLDivElement>(null);

  // Fetch customer data when component mounts or userId changes
  useEffect(() => {
    if (userId && !invoiceEditData) {
      dispatch(fetchInvoiceEditData(userId) as any);
    }
  }, [dispatch, userId, invoiceEditData]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setIsClientDropdownOpen(false);
      }
      if (pricingDropdownRef.current && !pricingDropdownRef.current.contains(event.target as Node)) {
        setIsPricingDropdownOpen(false);
      }
    };

    if (isClientDropdownOpen || isPricingDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isClientDropdownOpen, isPricingDropdownOpen]);

  // Reset form when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        clientDetails: '',
        taskDetails: '',
        fromDate: '',
        toDate: '',
        hours: '',
        pricingType: 'Hourly',
        price: ''
      });
      setValidationErrors({});
      setSelectedCustomerName('');
    }
  }, [isOpen]);

  // Reset hours when pricing type is not Hourly
  useEffect(() => {
    if (formData.pricingType !== 'Hourly') {
      setFormData(prev => ({
        ...prev,
        hours: '01:00'
      }));
    }
  }, [formData.pricingType]);

  // Function to convert HH:MM format to decimal hours
  const convertToDecimalHours = (timeString: string): number => {
    if (!timeString) return 0;
    
    const [hours, minutes] = timeString.split(':').map(Number);
    const decimalHours = hours + (minutes / 60);
    
    return Number(decimalHours.toFixed(2));
  };

  // Function to get article number and unit based on pricing type
  const getPricingTypeDetails = (pricingType: string) => {
    const pricingMap: { [key: string]: { unit: string; articleNumber: string } } = {
      'Hourly': { unit: 'Tim', articleNumber: '92' },
      'Payment per day': { unit: 'Dag', articleNumber: '94' },
      'Payment per week': { unit: 'vecka', articleNumber: '95' },
      'Payment per month': { unit: 'm', articleNumber: '96' },
      'Payment per Agreed': { unit: 'a', articleNumber: '97' }
    };
    
    return pricingMap[pricingType] || { unit: 'Tim', articleNumber: '92' };
  };

  // Function to generate date range between from and to dates
  const generateDateRange = (fromDate: string, toDate: string): string[] => {
    const dates: string[] = [];
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      dates.push(`${year}/${month}/${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return dates;
  };

  // Function to generate timesheet data array
  const generateTimesheetDataArray = () => {
    const pricingDetails = getPricingTypeDetails(formData.pricingType);
    const isHourly = formData.pricingType === 'Hourly';
    
    // Calculate working hours based on pricing type
    const workingHours = isHourly ? convertToDecimalHours(formData.hours) : 1;
    
    const dateRange = generateDateRange(formData.fromDate, formData.toDate);
    const priceValue = parseFloat(formData.price) || 0;
    
    // Determine number based on pricing type
    const number = isHourly ? workingHours : 1;
    
    // Calculate amount = number * amountPerUnit
    const amount = number * priceValue;
    
    // Get customer name (use stored name or find in customers array)
    const customerName = selectedCustomerName || '';
    
    const timesheetDataArray = dateRange.map((date) => ({
      id: 0,
      workingDate: date,
      dateId: date,
      number: number,
      unit: pricingDetails.unit,
      vat: 0,
      isArticleShownInInvoice: true,
      amountPerUnit: priceValue,
      amount: amount,
      articleNumber: pricingDetails.articleNumber,
      workingDescription: '',
      houseworkType: '',
      workingHour: workingHours,
      CustomWorkingDescription: formData.taskDetails,
      userId: userId || 0,
      customerId: parseInt(formData.clientDetails) || 0,
      customerName: customerName,
      }));
    
    return timesheetDataArray;
  };

  // Validation function
  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};

    // Customer validation
    if (!formData.clientDetails) {
      errors.clientDetails = t('CUSTOMER_REQUIRED') as string;
    }

    // Task Details validation
    if (!formData.taskDetails.trim()) {
      errors.taskDetails = t('TASK_DETAILS_REQUIRED') as string;
    } else {
      // Check for non-Latin characters
      const latinRegex = /^[a-zA-Z0-9\s.,!?;:'"-]+$/;
      if (!latinRegex.test(formData.taskDetails)) {
        errors.taskDetails = t('TASK_DETAILS_LATIN_ONLY') as string;
      }
      // Check character count (max 42 characters)
      else if (formData.taskDetails.length > 42) {
        errors.taskDetails = t('TASK_DETAILS_MAX_42_CHARACTERS') as string;
      }
    }

    // From Date validation
    if (!formData.fromDate) {
      errors.fromDate = t('FROM_DATE_REQUIRED') as string;
    }

    // To Date validation
    if (!formData.toDate) {
      errors.toDate = t('TO_DATE_REQUIRED') as string;
    } else if (formData.fromDate && formData.toDate < formData.fromDate) {
      errors.toDate = t('TO_DATE_MUST_BE_AFTER_FROM_DATE') as string;
    }

    // Hours validation
    if (!formData.hours) {
      errors.hours = t('HOURS_REQUIRED') as string;
    } else if (formData.pricingType === 'Hourly') {
      // Validate HH:MM format
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timeRegex.test(formData.hours)) {
        errors.hours = t('HOURS_INVALID_FORMAT') as string;
      } else {
        const [hours, minutes] = formData.hours.split(':').map(Number);
        if (hours > 23 || minutes > 59) {
          errors.hours = t('HOURS_MAX_23_59') as string;
        }
      }
    }

    // Pricing Type validation
    if (!formData.pricingType) {
      errors.pricingType = t('PRICING_TYPE_REQUIRED') as string;
    }

    // Price validation
    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = t('PRICE_REQUIRED') as string;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    // Generate and log the timesheet data array
    const timesheetDataArray = generateTimesheetDataArray();
    
    onSubmit(formData, timesheetDataArray);
    onClose();
  };

  const handleCancel = () => {
    setFormData({
      clientDetails: '',
      taskDetails: '',
      fromDate: '',
      toDate: '',
      hours: '',
      pricingType: 'Hourly',
      price: ''
    });
    setValidationErrors({});
    setSelectedCustomerName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="sidebar-overlay" onClick={onClose}></div>
      <div className="add-timesheet-sidebar">
        {/* Header */}
        <div className="sidebar-header">
          <h2 className="sidebar-title">{t('ADD_TIMESHEET_RECORD') || 'Add Timesheet Record'}</h2>
          <button className="sidebar-close-btn" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="sidebar-body">
          <div className="sidebar-form-content">
            {/* Client Details */}
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">{t('CLIENT_DETAILS') || 'Client Details'}</label>
                <div className="form-select-wrapper custom-dropdown" ref={clientDropdownRef}>
                  <div 
                    className={`dropdown-trigger ${isClientDropdownOpen ? 'open' : ''}`}
                    onClick={() => setIsClientDropdownOpen(!isClientDropdownOpen)}
                  >
                    <span className="dropdown-value">
                      {formData.clientDetails && customers.length > 0
                        ? customers.find((c: any) => String(c.customerId || c.id) === String(formData.clientDetails))?.name || t('SELECT_CLIENT')
                        : t('SELECT_CLIENT')}
                    </span>
                    <svg className={`dropdown-arrow ${isClientDropdownOpen ? 'rotated' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path 
                        d="M5 7.5L10 12.5L15 7.5" 
                        stroke="#68727D" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {isClientDropdownOpen && (
                    <div className="dropdown-list">
                      <div className="dropdown-content">
                        {loading ? (
                          <div className="list-item no-results">
                            <div className="text-input">
                              <div className="paragraph-container">
                                <p className="option-text">{t('LOADING') || 'Loading...'}</p>
                              </div>
                            </div>
                          </div>
                        ) : customers.length > 0 ? (
                          customers.map((customer: any) => (
                            <div
                              key={customer.customerId || customer.id}
                              className={`list-item ${String(formData.clientDetails) === String(customer.customerId || customer.id) ? 'selected' : ''}`}
                              onClick={() => {
                                const selectedId = String(customer.customerId || customer.id);
                                setSelectedCustomerName(customer.name);
                                setFormData({ ...formData, clientDetails: selectedId });
                                setValidationErrors({ ...validationErrors, clientDetails: undefined });
                                setIsClientDropdownOpen(false);
                              }}
                            >
                              <div className="text-input">
                                <div className="paragraph-container">
                                  <p className="option-text">{customer.name}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="list-item no-results">
                            <div className="text-input">
                              <div className="paragraph-container">
                                <p className="option-text">{t('NO_CLIENTS_FOUND')}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {validationErrors.clientDetails && (
                  <span className="error-message">{validationErrors.clientDetails}</span>
                )}
              </div>
            </div>

            {/* Task Details */}
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">{t('TASK_DETAILS') || 'Task Details'}</label>
                <div className="form-input-wrapper">
                  <input
                    type="text"
                    className="form-input"
                    value={formData.taskDetails}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only Latin characters, numbers, spaces, and basic punctuation
                      const latinRegex = /^[a-zA-Z0-9\s.,!?;:'"-]*$/;
                      // Only update if value is empty or matches Latin characters, and is 42 chars or less
                      if ((value === '' || latinRegex.test(value)) && value.length <= 42) {
                        setFormData({ ...formData, taskDetails: value });                        if (value.trim()) {
                          setValidationErrors({ ...validationErrors, taskDetails: undefined });
                        }                      }
                    }}
                    placeholder={t('ENTER_TASK_DETAILS') || 'Enter task details'}
                    maxLength={42}
                  />
                  <span style={{
                    fontSize: '12px',
                    color: formData.taskDetails.length > 40 ? '#dc3545' : '#68727d',
                    marginTop: '4px',
                    display: 'block'
                  }}>
                    {formData.taskDetails.length}/42 {t('CHARACTERS') || 'characters'}
                  </span>
                </div>
                {validationErrors.taskDetails && (
                  <span className="error-message">{validationErrors.taskDetails}</span>
                )}
              </div>
            </div>

            {/* From Date & To Date */}
            <div className="form-row form-row-split">
              <div className="form-field">
                <label className="form-label">{t('FROM_DATE') || 'From Date'}</label>
                <div className="form-input-wrapper">
                  <input
                    type="date"
                    className="form-input"
                    value={formData.fromDate}
                    onChange={(e) => {
                      setFormData({ ...formData, fromDate: e.target.value });
                      if (e.target.value) {
                        setValidationErrors({ ...validationErrors, fromDate: undefined });
                      }
                    }}
                    placeholder="dd/mm/yyyy"
                  />
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M6.66667 1.66666V4.16666M13.3333 1.66666V4.16666M2.91667 7.57499H17.0833M17.9167 7.08333V14.1667C17.9167 16.6667 16.6667 18.3333 13.75 18.3333H6.25C3.33333 18.3333 2.08333 16.6667 2.08333 14.1667V7.08333C2.08333 4.58333 3.33333 2.91666 6.25 2.91666H13.75C16.6667 2.91666 17.9167 4.58333 17.9167 7.08333Z" stroke="#68727D" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {validationErrors.fromDate && (
                  <span className="error-message">{validationErrors.fromDate}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-label">{t('TO_DATE') || 'To Date'}</label>
                <div className="form-input-wrapper">
                  <input
                    type="date"
                    className="form-input"
                    value={formData.toDate}
                    onChange={(e) => {
                      setFormData({ ...formData, toDate: e.target.value });
                      if (e.target.value) {
                        setValidationErrors({ ...validationErrors, toDate: undefined });
                      }
                    }}
                    placeholder="dd/mm/yyyy"
                  />
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M6.66667 1.66666V4.16666M13.3333 1.66666V4.16666M2.91667 7.57499H17.0833M17.9167 7.08333V14.1667C17.9167 16.6667 16.6667 18.3333 13.75 18.3333H6.25C3.33333 18.3333 2.08333 16.6667 2.08333 14.1667V7.08333C2.08333 4.58333 3.33333 2.91666 6.25 2.91666H13.75C16.6667 2.91666 17.9167 4.58333 17.9167 7.08333Z" stroke="#68727D" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {validationErrors.toDate && (
                  <span className="error-message">{validationErrors.toDate}</span>
                )}
              </div>
            </div>

            {/* Hours (HH:MM) */}
            <div className="form-row">
              <div className="form-field">
                <label className="form-label">{t('HOURS_HH_MM') || 'Hours (HH:MM)'}</label>
                <div className="form-input-wrapper">
                  <input
                    type="text"
                    className="form-input"
                    value={formData.hours}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Allow only numbers and colon
                      let timeValue = value.replace(/[^0-9:]/g, '');
                      
                      // Remove multiple colons, keep only first one
                      const colonCount = (timeValue.match(/:/g) || []).length;
                      if (colonCount > 1) {
                        const firstColonIndex = timeValue.indexOf(':');
                        timeValue = timeValue.slice(0, firstColonIndex + 1) + timeValue.slice(firstColonIndex + 1).replace(/:/g, '');
                      }
                      
                      // Parse hours and minutes
                      const timeParts = timeValue.split(':');
                      
                      if (timeParts.length === 1) {
                        // Only hours entered (no colon yet)
                        const hoursValue = timeParts[0].slice(0, 2);
                        if (hoursValue && parseInt(hoursValue) > 23) {
                          timeValue = '23';
                        } else {
                          timeValue = hoursValue;
                        }
                      } else if (timeParts.length === 2) {
                        // Hours and minutes
                        let hoursValue = timeParts[0].slice(0, 2);
                        let minutesValue = timeParts[1].slice(0, 2);
                        
                        if (hoursValue && parseInt(hoursValue) > 23) {
                          hoursValue = '23';
                        }
                        if (minutesValue && parseInt(minutesValue) > 59) {
                          minutesValue = '59';
                        }
                        
                        timeValue = hoursValue + ':' + minutesValue;
                      }
                      
                      setFormData({ ...formData, hours: timeValue });
                      if (timeValue) {
                        setValidationErrors({ ...validationErrors, hours: undefined });
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (!value || value === '') return;
                      
                      let formattedValue = value;
                      
                      if (!value.includes(':')) {
                        const hours = parseInt(value) || 0;
                        const validHours = Math.min(hours, 23);
                        formattedValue = `${String(validHours).padStart(2, '0')}:00`;
                      } else {
                        const [hoursStr, minsStr] = value.split(':');
                        const hours = parseInt(hoursStr) || 0;
                        const mins = parseInt(minsStr) || 0;
                        const validHours = Math.min(hours, 23);
                        const validMins = Math.min(mins, 59);
                        formattedValue = `${String(validHours).padStart(2, '0')}:${String(validMins).padStart(2, '0')}`;
                      }
                      
                      if (formattedValue !== value) {
                        setFormData({ ...formData, hours: formattedValue });
                      }
                    }}
                    placeholder="HH:MM"
                    disabled={formData.pricingType !== 'Hourly'}
                    style={{ opacity: formData.pricingType !== 'Hourly' ? 0.5 : 1, cursor: formData.pricingType !== 'Hourly' ? 'not-allowed' : 'text' }}
                  />
                  <svg className="input-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M18.3333 9.99999C18.3333 14.6024 14.6024 18.3333 9.99999 18.3333C5.39762 18.3333 1.66666 14.6024 1.66666 9.99999C1.66666 5.39762 5.39762 1.66666 9.99999 1.66666C14.6024 1.66666 18.3333 5.39762 18.3333 9.99999Z" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 5V10L13.3333 11.6667" stroke="#68727D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                {validationErrors.hours && (
                  <span className="error-message">{validationErrors.hours}</span>
                )}
              </div>
            </div>

            {/* Pricing Type & Price */}
            <div className="form-row form-row-split">
              <div className="form-field">
                <label className="form-label">{t('PRICING_TYPE') || 'Pricing Type'}</label>
                <div className="form-select-wrapper custom-dropdown" ref={pricingDropdownRef}>
                  <div 
                    className={`dropdown-trigger ${isPricingDropdownOpen ? 'open' : ''}`}
                    onClick={() => setIsPricingDropdownOpen(!isPricingDropdownOpen)}
                  >
                    <span className="dropdown-value">
                      {formData.pricingType === 'Hourly' && t('HOURLY')}
                      {formData.pricingType === 'Payment per day' && t('PAYMENT_PER_DAY')}
                      {formData.pricingType === 'Payment per week' && t('PAYMENT_PER_WEEK')}
                      {formData.pricingType === 'Payment per month' && t('PAYMENT_PER_MONTH')}
                      {formData.pricingType === 'Payment per Agreed' && t('PAYMENT_PER_AGREED')}
                    </span>
                    <svg className={`dropdown-arrow ${isPricingDropdownOpen ? 'rotated' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path 
                        d="M5 7.5L10 12.5L15 7.5" 
                        stroke="#68727D" 
                        strokeWidth="1.5" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {isPricingDropdownOpen && (
                    <div className="dropdown-list">
                      <div className="dropdown-content">
                        {[
                          { value: 'Hourly', label: t('HOURLY') },
                          { value: 'Payment per day', label: t('PAYMENT_PER_DAY') },
                          { value: 'Payment per week', label: t('PAYMENT_PER_WEEK') },
                          { value: 'Payment per month', label: t('PAYMENT_PER_MONTH') },
                          { value: 'Payment per Agreed', label: t('PAYMENT_PER_AGREED') }
                        ].map((option) => (
                          <div
                            key={option.value}
                            className={`list-item ${formData.pricingType === option.value ? 'selected' : ''}`}
                            onClick={() => {
                              setFormData({ ...formData, pricingType: option.value });
                              setValidationErrors({ ...validationErrors, pricingType: undefined });
                              setIsPricingDropdownOpen(false);
                            }}
                          >
                            <div className="text-input">
                              <div className="paragraph-container">
                                <p className="option-text">{option.label}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {validationErrors.pricingType && (
                  <span className="error-message">{validationErrors.pricingType}</span>
                )}
              </div>

              <div className="form-field">
                <label className="form-label">{t('PRICE') || 'Price'}</label>
                <div className="form-input-wrapper">
                  <input
                    type="number"
                    className="form-input"
                    value={formData.price}
                    onChange={(e) => {
                      setFormData({ ...formData, price: e.target.value });
                      if (e.target.value && parseFloat(e.target.value) > 0) {
                        setValidationErrors({ ...validationErrors, price: undefined });
                      }
                    }}
                    placeholder={t('ENTER_VALUE') || 'Enter value'}
                  />
                  <span className="input-currency">Kr</span>
                </div>
                {validationErrors.price && (
                  <span className="error-message">{validationErrors.price}</span>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sidebar-footer">
            <button type="button" className="btn-cancel" onClick={handleCancel}>
              {t('CANCEL') || 'Cancel'}
            </button>
            <button type="submit" className="btn-submit">
              {t('SUBMIT') || 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddTimesheetSidebar;
